import { z } from 'zod';
import { INTERNAL_ROLE_KEYS } from '../enums.js';
import { ORDERING_OPERATORS, isNumericConditionField, isOperatorAllowed, isReferenceConditionField, } from '../approvalConditions.js';
import { objectId, paginationQuery } from './common.js';
const isNumber = (value) => value !== '' && value !== null && typeof value !== 'boolean' && Number.isFinite(Number(value));
const isFilled = (value) => value !== undefined && value !== null && value !== '';
export const ruleCondition = z
    .object({
    field: z.enum([
        'amount',
        'vendorId',
        'departmentId',
        'locationId',
        'regionId',
        'verticalId',
        'businessUnitId',
        'currency',
        'employeeCount',
    ]),
    operator: z.enum(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'nin', 'between']),
    value: z.unknown(),
})
    // A condition the engine cannot evaluate never matches, so the rule is dead
    // and what it should catch is auto-approved. Refuse those at the door.
    .superRefine((condition, context) => {
    if (!isOperatorAllowed(condition.field, condition.operator)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['operator'],
            message: isReferenceConditionField(condition.field)
                ? 'A vendor, department, location, region, vertical or business unit can only be ' +
                    '"is", "is not", "is one of" or "is not one of" — it has no order to compare'
                : `"${condition.field}" cannot be compared with "${condition.operator}"`,
        });
        return;
    }
    const { operator, value } = condition;
    const valid = operator === 'between'
        ? Array.isArray(value) && value.length === 2 && value.every(isNumber)
        : operator === 'in' || operator === 'nin'
            ? Array.isArray(value) && value.length > 0 && value.every(isFilled)
            : ORDERING_OPERATORS.includes(operator) ||
                isNumericConditionField(condition.field)
                ? isNumber(value)
                : isFilled(value);
    if (!valid) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: operator === 'between'
                ? 'Enter both amounts of the range'
                : operator === 'in' || operator === 'nin'
                    ? 'Choose at least one value'
                    : isNumericConditionField(condition.field)
                        ? 'Enter a number'
                        : 'Choose a value',
        });
    }
});
export const ruleStep = z
    .object({
    order: z.number().int().min(1),
    // DEPARTMENT_HEAD and VERTICAL_HEAD resolve from the subject, so neither
    // needs a roleKey or userId.
    approverType: z.enum(['ROLE', 'USER', 'DEPARTMENT_HEAD', 'VERTICAL_HEAD']),
    // A vendor's own login can never be an approver.
    roleKey: z.enum(INTERNAL_ROLE_KEYS).optional(),
    userId: objectId.optional(),
    label: z.string().trim().max(120).optional(),
    slaHours: z.number().int().min(1).max(720).optional(),
})
    .refine((step) => step.approverType !== 'ROLE' || !!step.roleKey, {
    message: 'Choose the role that approves this step',
    path: ['roleKey'],
})
    .refine((step) => step.approverType !== 'USER' || !!step.userId, {
    message: 'Choose the person who approves this step',
    path: ['userId'],
});
export const createApprovalRuleRequest = z.object({
    companyId: objectId,
    name: z.string().trim().min(2).max(160),
    description: z.string().trim().max(500).optional(),
    appliesTo: z.enum(['VENDOR_INVOICE', 'PAYROLL_BATCH']),
    priority: z.number().int().min(0).max(1000).default(100),
    active: z.boolean().default(true),
    conditions: z.array(ruleCondition).max(20).default([]),
    steps: z.array(ruleStep).min(1, 'At least one approval step').max(10),
});
export const updateApprovalRuleRequest = createApprovalRuleRequest
    .omit({ companyId: true })
    .partial();
export const approvalActionRequest = z.object({
    action: z.enum(['APPROVE', 'REJECT']),
    comment: z.string().trim().max(1000).optional(),
});
export const approvalCommentRequest = z.object({
    comment: z.string().trim().min(1).max(1000),
});
export const approvalListQuery = paginationQuery.extend({
    companyId: objectId.optional(),
    subjectType: z.enum(['VENDOR_INVOICE', 'PAYROLL_BATCH']).optional(),
    status: z.enum(['PENDING', 'IN_PROGRESS', 'APPROVED', 'REJECTED', 'CANCELLED']).optional(),
    /** `MINE` restricts to requests where the caller is an eligible approver. */
    scope: z.enum(['MINE', 'ALL']).default('MINE'),
});
//# sourceMappingURL=approval.js.map